import React, { FunctionComponent, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import PersistentDrawerLeft from "./SideNavBar";
import Navbar from "../Navbar/Navbar";

/**
 * Props for AnalyticsSideNav
 *
 * This component serves as a wrapper that includes:
 *  - The top navigation bar
 *  - The left sidebar (drawer)
 *
 * It supports responsive behavior:
 *  - Sidebar becomes a drawer on mobile
 *  - Sidebar stays open persistently on desktop
 */
type props = {
  props?: any;
  title:
    | "Organizations"
    | "Contacts"
    | "Users"
    | "Bookings"
    | "Drilldown"
    | "Team";
  path: string;
  leadManger: Boolean;
  operationManger: Boolean;

  project?: string;
  task?: string;
  resources?: string;
  callLogs?: string;
  faq?: string;
  api?: string;
  news?: string;
  apiData?: string;
  leadDistributor?: string;
  customButton?: string;
  updatePassword?: string;
};

/**
 * AnalyticsSideNav
 * ----------------
 * Unified layout wrapper for analytics pages.
 *
 * Responsibilities:
 *  - Render Navbar on top
 *  - Render Side Navigation (drawer on mobile, persistent on desktop)
 *  - Handle responsive drawer toggling
 */
const AnalyticsSideNav: FunctionComponent<props> = ({
  props,
  title,
  path,
  leadManger,
  operationManger,
  project,
  task,
  resources,
  callLogs,
  faq,
  api,
  news,
  apiData,
  leadDistributor,
  customButton,
  updatePassword
}) => {

  /** Detect if screen is mobile (≤ 900px) */
  const isMobile = useMediaQuery("(max-width: 900px)");

  /** Tracks drawer open/close state (only used on mobile) */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /** Toggle drawer (mobile only) */
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  /** Close drawer when selecting a menu item (mobile only) */
  const handleMenuItemClick = () => {
    if (isMobile) setDrawerOpen(false);
  };

  return (
    <>
      {/* =========================================
          TOP NAVBAR
          - Always visible
          - Drawer toggle visible only on mobile
        ========================================= */}
      <Navbar
        {...props}
        title={title}
        path={path}
        leadManger={leadManger}
        project={project}
        task={task}
        resources={resources}
        callLogs={callLogs}
        faq={faq}
        user={props?.user}
        api={api}
        news={news}
        apiData={apiData}
        leadDistributor={leadDistributor}
        customButton={customButton}
        theme={props?.theme}
        userRole={props?.userRole}
        open={isMobile ? drawerOpen : true}
        contacts={props?.contacts}
        onDrawerToggle={isMobile ? handleDrawerToggle : undefined}
        isMobile={isMobile}
      />

      {/* =========================================
          SIDE NAVIGATION
          - Drawer mode on mobile
          - Persistent sidebar on desktop
        ========================================= */}
      <PersistentDrawerLeft
        props={props}
        title={title}
        path={path}
        leadManger={leadManger}
        operationManger={operationManger}
        project={project}
        task={task}
        resources={resources}
        callLogs={callLogs}
        faq={faq}
        apiData={apiData}
        leadDistributor={leadDistributor}
        api={api}
        news={news}
        customButton={customButton}
        updatePassword={updatePassword}
        drawerOpen={isMobile ? drawerOpen : true}
        onDrawerToggle={isMobile ? handleDrawerToggle : undefined}
        isMobile={isMobile}
        onMenuItemClick={handleMenuItemClick}
      />
    </>
  );
};

export default AnalyticsSideNav;
